/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-29 10:22:56
 * @ Modified by: David Helmick
 * @ Modified time: 2024-08-02 11:50:47
 * @ Description: Page for adding a shared marketplace
 */

import React, { useState } from 'react'

import {
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Checkbox,
  List,
  ListItem,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material'

import {
  pageShellWrapperSx,
  pageShellMainSx,
  pageShellContentSx,
  pageShellInnerSx,
} from '@/layout-blueprints/chromeSx'

// Centralized lodash imports - much cleaner!
import {
  includes,
  remove,
  map,
  find,
  compact,
  isEmpty,
  get,
  merge,
  cloneDeep,
} from '@/utils/lodash'

import housefoliosLogo from '@/assets/images/HousefoliosCloudTransparent.png'
import { useMutation } from '@apollo/client/react'

import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import withSession from '@/housefolios-components/Session/withSession'
import { ADD_SHARED_MARKETPLACES } from '../mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { enqueueSnackbar } from 'notistack'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import useAffiliate from '@/hooks/useAffiliate'
import { useNavigate } from 'react-router'

function eligibleOrgIds(roles) {
  return compact(
    map(roles, (roleOrg) => {
      if (
        roleOrg.organization &&
        roleOrg.role[0] !== 'deactive' &&
        roleOrg.organization._id !== 'superAdmin'
      )
        return roleOrg.organization._id
    }),
  )
}

function AddSharedMarketplaceComponent(props) {
  const eligibleIds = eligibleOrgIds(props.session.me.roles)
  const [selected, setSelected] = useState([])
  const [loader, setLoader] = React.useState(false)
  const [confirmAllOrgs, setConfirmAllOrgs] = useState(false)

  const [addSharedMarketplaces] = useMutation(ADD_SHARED_MARKETPLACES)

  const urlParams = new URLSearchParams(window.location.search)
  const token = props.token || urlParams.get('sharedMarketplaceToken')

  const toggleOrg = (orgId) => {
    let newSelected = [...selected]
    if (includes(newSelected, orgId))
      remove(newSelected, (select) => select === orgId)
    else newSelected.push(orgId)
    setSelected(newSelected)
  }

  const [{ affiliate, affiliateLoading, urlAffiliate }] =
    useAffiliate({})

  const selectAll = () => {
    if (selected.length < eligibleIds.length) setSelected(eligibleIds)
    else setSelected([])
  }

  const preferences = merge(
    cloneDeep(DEFAULT_PREFERENCES),
    props?.session?.me?.activeOrg?.memberPreferences,
    props?.session?.me?.activeOrg?.preferences,
    props.session.me.preferences,
  )

  const navigate = useNavigate()

  const finishAdd = () => {
    if (props.handleClose) props.handleClose()
    else
      navigate(
        get(preferences, 'general.defaultPage.value') || '/launchpad',
      )
  }

  const runAdd = async () => {
    setLoader(true)
    try {
      await addSharedMarketplaces({
        variables: {
          token,
          orgIds: selected,
        },
        refetchQueries: [{ query: GET_ME }],
      })
      enqueueSnackbar(
        selected.length === 1
          ? `Added shared marketplace to org ${
              find(
                props.session.me.roles,
                (role) => role.organization._id === selected[0],
              )?.organization?.name || ''
            }`
          : `Added shared marketplace to ${selected.length} organizations`,
        {
          variant: 'success',
          autoHideDuration: 3000,
        },
      )
      finishAdd()
    } catch (error) {
      const message = error.message
      if (includes(message, 'expired'))
        enqueueSnackbar('Token has expired', {
          variant: 'error',
          autoHideDuration: 3000,
        })
      else if (includes(message, 'user'))
        enqueueSnackbar('Token intended for a different user', {
          variant: 'error',
          autoHideDuration: 3000,
        })
      else if (includes(message, 'Org'))
        enqueueSnackbar('Org owns this marketplace', {
          variant: 'error',
          autoHideDuration: 3000,
        })
      else if (includes(message, 'Marketplace'))
        enqueueSnackbar('Marketplace already added', {
          variant: 'error',
          autoHideDuration: 3000,
        })
      else
        enqueueSnackbar('Token is invalid', {
          variant: 'error',
          autoHideDuration: 3000,
        })
      console.log(error)
    } finally {
      setLoader(false)
      setConfirmAllOrgs(false)
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    if (!token || isEmpty(selected)) return
    if (
      selected.length === eligibleIds.length &&
      eligibleIds.length > 1
    ) {
      setConfirmAllOrgs(true)
      return
    }
    await runAdd()
  }

  if (affiliateLoading)
    return (
      <Backdrop open={true} style={{ color: '#fff', zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )

  if (!token) {
    return (
      <Box sx={props.handleClose ? undefined : pageShellWrapperSx}>
        <Box sx={props.handleClose ? undefined : pageShellMainSx}>
          <Box sx={pageShellContentSx}>
            <Box sx={pageShellInnerSx}>
              <Box
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={[{ py: '3rem' }, { position: "relative", zIndex: "7", width: "100%" }]}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        style={{ fontWeight: 600 }}
                      >
                        Missing share link
                      </Typography>
                      <Typography sx={{ mt: '0.5rem' }}>
                        This marketplace share link is missing a
                        token. Ask the sender to share the marketplace
                        again.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={
                          props.handleClose
                            ? props.handleClose
                            : () =>
                                navigate(
                                  get(
                                    preferences,
                                    'general.defaultPage.value',
                                  ) || '/launchpad',
                                )
                        }
                        sx={{ mt: '1rem' }}
                      >
                        {props.handleClose ? 'Close' : 'Continue'}
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box sx={props.handleClose ? undefined : pageShellWrapperSx}>
        <Box sx={props.handleClose ? undefined : pageShellMainSx}>
          <Box sx={pageShellContentSx}>
            <Box sx={pageShellInnerSx}>
              <Box
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={[{ py: '3rem' }, { position: "relative", zIndex: "7", width: "100%" }]}>
                  <Card>
                    <CardContent>
                      <Grid container direction="column" spacing={4}>
                        <Grid>
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            sx={{
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Grid>
                              <Grid container direction="column">
                                <Grid>
                                  <Typography
                                    variant="h6"
                                    style={{ fontWeight: 600 }}
                                  >
                                    Add Shared Marketplace
                                  </Typography>
                                </Grid>
                                <Grid>
                                  <Typography>
                                    Choose the organizations to add
                                    the shared marketplace to
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid>
                              <img
                                src={
                                  urlAffiliate
                                    ? get(
                                        affiliate,
                                        'sidebar.sidebarMemberSmallImage.0.url',
                                      ) || housefoliosLogo
                                    : housefoliosLogo
                                }
                                alt="Housefolios"
                                style={{ width: 60, height: 50 }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        {/* <Grid>
                        <Button
                          variant='outlined'
                          fullWidth={true}
                        >
                          <FontAwesomeIcon
                            style={{ marginRight: 10 }}
                            icon={['fab', 'google']}
                            size="1x"
                          />
                          <Typography>SIGN IN WITH GOOGLE</Typography>
                        </Button>
                      </Grid>
                      <Grid style={{ textAlign: 'center' }}>
                        <Typography>OR</Typography>
                      </Grid> */}
                        <Grid>
                          <BlockUi
                            blocking={loader}
                            loader={
                              <PacmanLoader
                                color={'var(--primary)'}
                                loading={loader}
                              />
                            }
                            message={
                              <Box sx={{ color: '#368cff' }}>
                                Adding Shared Marketplace
                              </Box>
                            }
                          >
                            <Grid
                              container
                              direction="row"
                              spacing={2}
                            >
                              <Grid size={12}>
                                <Box
                                  style={{ width: '100%' }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box sx={[{
                                    height: "50px !important",
                                    width: "50px !important",
                                    lineHeight: "50px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                    mr: "1rem",
                                  }, {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mr: "0.25rem",
                                    position: "relative",
                                    fontSize: "1.2rem",
                                    transition: "all 0.2s ease-in-out",
                                    "&:last-child": { mr: 0, marginRight: 0 },
                                    "& .badge": { position: "absolute", right: 0, bottom: 0 },
                                    "& svg, & .svg-inline--fa": { display: "block" },
                                    marginRight: "0.25rem",
                                  }]}>
                                    <Checkbox
                                      checked={
                                        selected.length ===
                                          eligibleIds.length &&
                                        eligibleIds.length > 0
                                      }
                                      indeterminate={
                                        !isEmpty(selected) &&
                                        selected.length <
                                          eligibleIds.length
                                      }
                                      //onChange={() => toggleOrg(roleOrg.organization._id)}
                                      onClick={selectAll}
                                    />
                                  </Box>
                                  <div>
                                    <Box sx={[{ fontWeight: 700 }, {
                                      fontSize: "0.875rem !important",
                                      color: "#070919",
                                      "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                    }]}>
                                      Select All
                                    </Box>
                                    {/* <div>
                                    <div style={{ backgroundColor: '#797d95' }}>Completed</div>
                                    <div style={{ color: '#797d95' }}>Not Selected</div>
                                  </div> */}
                                  </div>
                                  <div style={{ flex: 1 }} />
                                  <div>
                                    {/* <Chip color="primary" label="Share marketplace" 
                                      // onClick={(e) => {
                                      //   toggleOrg(roleOrg.organization._id)
                                      // }}
                                  /> */}
                                  </div>
                                </Box>
                              </Grid>
                              <Grid size={12}>
                                <List
                                  component="div"
                                  sx={{
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    "& .MuiListItem-root, & .MuiListItemButton-root": {
                                      width: "100%",
                                      padding: "0.75rem 1.25rem",
                                      backgroundColor: "#fff",
                                      border: "1px solid rgba(0, 0, 0, 0.125)",
                                      borderRadius: 0,
                                      margin: 0,
                                      borderWidth: "0 0 1px",
                                      "&.MuiListItem-button": {
                                        borderRadius: 0,
                                        borderWidth: "0 0 1px",
                                        "& .MuiTouchRipple-root": { opacity: 0.2 },
                                      },
                                      "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                                      "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                                      "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                                      "&:first-of-type": { borderTop: "0 !important" },
                                      "&:last-child": { borderBottom: "0 !important" },
                                    },
                                  }}
                                  style={{
                                    height: 400,
                                    overflow: 'scroll',
                                  }}
                                >
                                  {map(
                                    props.session.me.roles,
                                    (roleOrg) => {
                                      if (
                                        roleOrg.organization &&
                                        roleOrg.role[0] !==
                                          'deactive' &&
                                        roleOrg.organization._id !==
                                          'superAdmin'
                                      ) {
                                        return (
                                          <ListItem
                                            component="a"
                                            button
                                            href={null}
                                            disableRipple
                                            margin="dense"
                                            onClick={(e) => {
                                              toggleOrg(
                                                roleOrg.organization
                                                  ._id,
                                              )
                                            }}
                                           
                                            sx={{
                                              transition: "all 0.2s ease-in-out !important",
                                              transform: "scale(1) !important",
                                              boxShadow: "none",
                                              position: "relative",
                                              zIndex: "0",
                                              "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                                              display: "flex",
                                              backgroundColor: "#fff",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Box
                                              style={{
                                                width: '100%',
                                              }}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Box sx={[{
                                                height: "50px !important",
                                                width: "50px !important",
                                                lineHeight: "50px !important",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                p: 0,
                                                mr: "1rem",
                                              }, {
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mr: "0.25rem",
                                                position: "relative",
                                                fontSize: "1.2rem",
                                                transition: "all 0.2s ease-in-out",
                                                "&:last-child": { mr: 0, marginRight: 0 },
                                                "& .badge": { position: "absolute", right: 0, bottom: 0 },
                                                "& svg, & .svg-inline--fa": { display: "block" },
                                                marginRight: "0.25rem",
                                              }]}>
                                                <Checkbox
                                                  style={{
                                                    marginRight: 8,
                                                  }}
                                                  checked={includes(
                                                    selected,
                                                    roleOrg
                                                      .organization
                                                      ._id,
                                                  )}
                                                  //onChange={() => toggleOrg(roleOrg.organization._id)}
                                                />
                                              </Box>
                                              <div>
                                                <Box sx={[{ fontWeight: 700, }, {
                                                  fontSize: "0.875rem !important",
                                                  color: "#070919",
                                                  "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                                }]}>
                                                  {
                                                    roleOrg
                                                      .organization
                                                      .name
                                                  }
                                                </Box>
                                                {/* <div>
                                              <div style={{ backgroundColor: '#797d95' }}>Completed</div>
                                              <div style={{ color: '#797d95' }}>Not Selected</div>
                                            </div> */}
                                              </div>
                                              <div
                                                style={{ flex: 1 }}
                                              />
                                              <div>
                                                {/* <Chip color="primary" label="Share marketplace" 
                                                // onClick={(e) => {
                                                //   toggleOrg(roleOrg.organization._id)
                                                // }}
                                            /> */}
                                              </div>
                                            </Box>
                                          </ListItem>
                                        )
                                      }
                                    },
                                  )}
                                </List>
                              </Grid>
                              <Grid size={12}>
                                <Button
                                  variant="contained"
                                  type="submit"
                                  // color='primary'
                                  fullWidth={true}
                                  onClick={onSubmit}
                                  style={{
                                    backgroundColor: '#04a3e3',
                                    color: 'white',
                                  }}
                                  disabled={isEmpty(selected)}
                                >
                                  <Typography>
                                    Add Marketplace
                                  </Typography>
                                </Button>
                              </Grid>
                              <Grid size={12}>
                                <Button
                                  variant="contained"
                                  fullWidth={true}
                                  onClick={
                                    props.handleClose
                                      ? props.handleClose
                                      : () =>
                                          navigate(
                                            get(
                                              preferences,
                                              'general.defaultPage.value',
                                            ) || '/launchpad',
                                          )
                                  }
                                >
                                  <Typography>
                                    {props.handleClose
                                      ? 'Close'
                                      : 'Continue to Launchpad'}
                                  </Typography>
                                </Button>
                              </Grid>
                            </Grid>
                          </BlockUi>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={confirmAllOrgs}
        onClose={() => setConfirmAllOrgs(false)}
      >
        <DialogTitle>Add to every organization?</DialogTitle>
        <DialogContent>
          <Typography>
            This will add the shared marketplace to all{' '}
            {eligibleIds.length} of your organizations.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOrgs(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={runAdd}
            disabled={loader}
          >
            Add to all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withSession(AddSharedMarketplaceComponent)
